/**
 * Lightweight rolling console capture for bug reports.
 *
 * When a user reports a bug and opts in to "include technical logs", we attach
 * the most recent console output so IT can see what the browser logged around
 * the problem. We keep a small ring buffer and never send it anywhere unless
 * the user explicitly ticks the box on the feedback form.
 */

const MAX_ENTRIES = 120;
/** @type {{ level: string, time: string, text: string }[]} */
const buffer = [];
let installed = false;

function push(level, args) {
  try {
    const text = args
      .map((a) => {
        if (typeof a === 'string') return a;
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      })
      .join(' ')
      .slice(0, 800);
    buffer.push({ level, time: new Date().toISOString(), text });
    if (buffer.length > MAX_ENTRIES) buffer.shift();
  } catch {
    /* capture must never break logging */
  }
}

/** Patch console methods to tee into the ring buffer. Call once at startup. */
export function installConsoleCapture() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  for (const level of ['log', 'info', 'warn', 'error']) {
    const original = console[level]?.bind(console);
    if (!original) continue;
    console[level] = (...args) => {
      push(level, args);
      original(...args);
    };
  }
  window.addEventListener('error', (e) => push('error', [`Uncaught: ${e.message} (${e.filename}:${e.lineno})`]));
  window.addEventListener('unhandledrejection', (e) => push('error', [`Unhandled promise rejection: ${e.reason}`]));
}

/** Return the captured log lines as a single formatted string. */
export function getCapturedLogs() {
  return buffer
    .map((e) => `[${e.time}] ${e.level.toUpperCase()}: ${e.text}`)
    .join('\n');
}
